import type { AppManifest, EntityDef, FieldDef } from '@colplat/schema';
import type {
  ClientMessage, FieldKeyString, FieldValue, FieldVersion, Hlc, PresenceUser, RejectReason, ServerMessage, Uuid,
} from '@colplat/protocol';
import {
  HlcGenerator, fieldKey, parseFieldKey, recordLeaseKey, sameFieldValue,
} from '@colplat/protocol';
import type { Transport, TransportState } from './transport.js';
import type { LeaseClient, LeaseOutcome } from './leaseClient.js';
import type { OutboxQueue } from './queue.js';
import type { Replica } from './replica.js';
import type { ReviewStore, PendingRevision, StaleBaseReview } from './review.js';

/**
 * The composition root. Reproduces SPEC §6.1's field-state vocabulary
 * verbatim — deliberately no `disabled` anywhere in the public surface (hard
 * invariant 4): `ui-web` maps `locked` to `readonly` + `aria-readonly`.
 */
export type FieldState =
  | { kind: 'system' }
  | { kind: 'idle' }
  | { kind: 'claiming' }
  | { kind: 'mine' }
  /**
   * Held by somebody else — another person, or another tab of this one.
   * `online` false is an unfinished edit whose holder is not connected
   * ("unfinished since …"); holds no longer lapse (ADR-0003, 2026-10-22), so
   * Take over is offered either way.
   */
  | { kind: 'locked'; holder: Uuid; online: boolean; since?: string }
  /**
   * SPEC §6.1's "taken off you": held by `holder` now, who took it from this
   * tab while something was typed here. That value is kept (`FieldRead.held`)
   * and shown beneath the control with Keep mine.
   */
  | { kind: 'takenOffYou'; holder: Uuid; online: boolean; since?: string }
  | { kind: 'remotelyChanged' }
  | { kind: 'needsReview' };

export interface FieldRead {
  value: FieldValue | undefined;
  state: FieldState;
  version: number;
  history: FieldVersion[];
  /** What this tab had typed when the field was taken off it, until it is kept or written. */
  held?: FieldValue;
}

export interface DocumentCommitOutcome {
  ok: boolean;
  reason?: RejectReason;
}

const RECENTLY_CHANGED_MS = 4000;

export interface SessionOptions {
  me: PresenceUser;
  manifest: AppManifest;
  transport: Pick<Transport, 'send' | 'onMessage' | 'onStateChange'>;
  leaseClient: Pick<LeaseClient, 'state' | 'claim' | 'steal' | 'release' | 'subscribe'>;
  outbox: Pick<OutboxQueue, 'enqueue' | 'enqueueRecord' | 'all' | 'remove'>;
  replica: Pick<
    Replica,
    'getField' | 'getRecord' | 'getHistory' | 'ensureRecordLoaded' | 'applyCommitted' | 'recordHistoryOnly' | 'subscribe'
  >;
  review: Pick<
    ReviewStore,
    'listStaleBase' | 'listPendingRevisions' | 'loadPendingRevisions' | 'resolveStaleBase' | 'resolvePendingRevision' | 'addStaleBase' | 'subscribe'
  >;
  node: Uuid;
  /** Document-mode commits travel over REST, not the WebSocket (ADR-0001: sync must never do transactional work). */
  commitDocumentRest: (req: {
    entity: string; recordId: Uuid; fields: Record<string, FieldValue>; baseVersion: number; leaseId: Uuid;
  }) => Promise<DocumentCommitOutcome>;
  now?: () => number;
}

export class Session {
  private readonly hlc: HlcGenerator;
  /** A remote commit for a field I currently hold — queued, never repaints the live value (hard invariant 5). */
  private readonly queuedRemote = new Map<FieldKeyString, FieldVersion>();
  private readonly recentlyChanged = new Map<FieldKeyString, ReturnType<typeof setTimeout>>();
  /** Who holds each key this tab knows of, from `leaseTaken`/`leaseFreed`, a refused claim, or opening the record. */
  private readonly locks = new Map<FieldKeyString, { holder: Uuid; online: boolean; since?: string }>();
  /** The last value typed into the field this tab holds — what a take-over must not lose. */
  private readonly drafts = new Map<FieldKeyString, FieldValue>();
  /** Values kept from fields taken off this tab (SPEC §6.1, "taken off you"). */
  private readonly heldOff = new Map<FieldKeyString, FieldValue>();
  /** The key this tab last held — a take-over names the person, not the tab, so this says it was this tab. */
  private heldKey: FieldKeyString | undefined;
  private readonly presenceByRecord = new Map<string, PresenceUser[]>();
  private readonly listeners = new Set<() => void>();
  private readonly noticeListeners = new Set<(message: string) => void>();
  private online = false;
  private readonly unsubscribers: Array<() => void> = [];

  constructor(private readonly opts: SessionOptions) {
    this.hlc = new HlcGenerator(opts.node, opts.now);
    this.unsubscribers.push(opts.transport.onMessage((msg) => { void this.onMessage(msg); }));
    this.unsubscribers.push(opts.transport.onStateChange((s) => this.onTransportStateChange(s)));
    this.unsubscribers.push(opts.leaseClient.subscribe((s) => {
      if (s.phase === 'held' || s.phase === 'disconnected') this.heldKey = s.key;
      this.notify();
    }));
    this.unsubscribers.push(opts.replica.subscribe(() => this.notify()));
    this.unsubscribers.push(opts.review.subscribe(() => this.notify()));
  }

  get isOnline(): boolean { return this.online; }

  /**
   * A fresh HLC timestamp from this session's generator.
   *
   * For writes an app queues itself — webledger's offline transaction
   * creates go through `OutboxQueue.enqueueDocument` rather than through this
   * class, because a document-*group* commit travels over REST (ADR-0021) and
   * `Session` has no path for one. They must still be stamped by the same
   * generator as everything else, or replay ordering would be comparing
   * clocks from two different sources — invariant #6 is about one causal
   * order per session, not one per code path.
   */
  nextClock(): Hlc { return this.hlc.send(); }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** External-store contract (`useSyncExternalStore`-compatible). Reads go through `field()`/`presence()`. */
  getSnapshot(): unknown { return undefined; }

  /** Wired to an `aria-live="polite"` region (hard invariant 11: state changes are never assertive). */
  onNotice(fn: (message: string) => void): () => void {
    this.noticeListeners.add(fn);
    return () => this.noticeListeners.delete(fn);
  }

  dispose(): void {
    for (const timer of this.recentlyChanged.values()) clearTimeout(timer);
    for (const unsub of this.unsubscribers) unsub();
  }

  // ------------------------------------------------------------------ reads

  field(entity: string, recordId: Uuid, fieldName: string): FieldRead {
    const key = fieldKey(entity, recordId, fieldName);
    const fieldDef = this.requireField(this.requireEntity(entity), fieldName);
    const snap = this.opts.replica.getField(key);
    const history = this.opts.replica.getHistory(key);

    const lock = this.locks.get(key);
    const held = this.heldOff.get(key);
    let state: FieldState;
    if (fieldDef.system) {
      state = { kind: 'system' };
    } else if (this.opts.review.listStaleBase(entity, recordId).some((r) => r.key === key)) {
      state = { kind: 'needsReview' };
    } else if (this.isMine(key)) {
      state = this.opts.leaseClient.state.phase === 'claiming' ? { kind: 'claiming' } : { kind: 'mine' };
    } else if (lock) {
      state = {
        kind: held ? 'takenOffYou' : 'locked', holder: lock.holder, online: lock.online,
        ...(lock.since !== undefined ? { since: lock.since } : {}),
      };
    } else if (this.recentlyChanged.has(key)) {
      state = { kind: 'remotelyChanged' };
    } else {
      state = { kind: 'idle' };
    }

    return { value: snap?.value, state, version: snap?.version ?? 0, history, ...(held ? { held } : {}) };
  }

  presence(entity: string, recordId: Uuid): PresenceUser[] {
    return this.presenceByRecord.get(`${entity}:${recordId}`) ?? [];
  }

  pendingReviews(entity?: string, recordId?: Uuid): StaleBaseReview[] {
    return this.opts.review.listStaleBase(entity, recordId);
  }

  pendingRevisions(entity: string, recordId: Uuid): PendingRevision[] {
    return this.opts.review.listPendingRevisions(entity, recordId);
  }

  // -------------------------------------------------------------- lifecycle

  /**
   * `openRecord` alone — deliberately no accompanying `subscribe`.
   *
   * The server tracks *why* a connection wants a record's fan-out
   * ('subscribe' for a list watching many rows, 'open' for a detail view) and
   * merges the two into 'both', precisely so dropping one does not stop
   * delivery while the other is live (services/sync's `Registry`). Sending
   * `subscribe` here, and `unsubscribe` in `closeRecord`, defeated that: a
   * list subscribed to 250 rows lost its subscription for whichever row the
   * user opened and closed a modal on, because `closeRecord`'s `unsubscribe`
   * cancelled a subscription the *list* owned and this call never made.
   *
   * The symptom was a lock silently failing to appear on that row ever again
   * — the list's `subscribedIds` still listed it, so nothing re-subscribed
   * either, and only a reload or a reconnect brought it back. `openRecord`
   * registers fan-out under 'open' on its own, so nothing is lost by not
   * asking twice. Whoever calls `subscribe` owns the matching `unsubscribe`.
   */
  async openRecord(entity: string, recordId: Uuid): Promise<void> {
    // `hold` is this session's answer to invariant #5: a field being edited
    // here keeps what is in the box, whatever the server says. Nothing is
    // normally held at this point — a record is opened before its form is
    // shown — but `openRecord` is public and a caller may have a lease
    // elsewhere in the same record.
    await this.opts.replica.ensureRecordLoaded(entity, recordId, (key) => this.isMine(key));
    this.opts.transport.send({ t: 'openRecord', entity, recordId });
    await this.opts.review.loadPendingRevisions(entity, recordId);
    this.notify();
  }

  /** Releases any lease held inside this record — an EditModal is a lease scope. */
  closeRecord(entity: string, recordId: Uuid): void {
    const leaseState = this.opts.leaseClient.state;
    if (leaseState.phase !== 'idle' && leaseState.phase !== 'releasing') {
      const parsed = parseFieldKey(leaseState.key);
      if (parsed.entity === entity && parsed.recordId === recordId) {
        void this.opts.leaseClient.release(leaseState.key);
      }
    }
    // No `unsubscribe` — see `openRecord`. This would cancel a subscription
    // belonging to whatever list is showing this record behind the modal.
    this.opts.transport.send({ t: 'closeRecord', entity, recordId });
    this.presenceByRecord.delete(`${entity}:${recordId}`);
    // What was held from this record goes with its form.
    for (const key of [...this.heldOff.keys(), ...this.drafts.keys()]) {
      const parsed = parseFieldKey(key);
      if (parsed.entity === entity && parsed.recordId === recordId) {
        this.heldOff.delete(key);
        this.drafts.delete(key);
      }
    }
    this.notify();
  }

  // ------------------------------------------------------------- field-mode

  async claim(entity: string, recordId: Uuid, fieldName: string): Promise<LeaseOutcome> {
    const key = fieldKey(entity, recordId, fieldName);
    const outcome = await this.opts.leaseClient.claim(key);
    // Refused because it is held: now we know by whom, which a tab that opened
    // the record before the hold was taken might not have heard.
    if (!outcome.ok && outcome.reason === 'denied' && outcome.deniedBecause === 'held') {
      this.locks.set(key, {
        holder: outcome.holder, online: outcome.online ?? true,
        ...(outcome.since !== undefined ? { since: outcome.since } : {}),
      });
    } else if (outcome.ok) {
      this.locks.delete(key);
    }
    this.notify();
    return outcome;
  }

  /**
   * Take a field somebody else holds — always offered, because losing it is
   * survivable: whoever had it keeps what they typed (ADR-0003, 2026-09-12).
   */
  async takeOver(entity: string, recordId: Uuid, fieldName: string): Promise<LeaseOutcome> {
    const key = fieldKey(entity, recordId, fieldName);
    const outcome = await this.opts.leaseClient.steal(key);
    if (outcome.ok) this.locks.delete(key);
    this.notify();
    return outcome;
  }

  /**
   * "Keep mine": take the field back and write the value kept from before it
   * was taken off this tab, then let it go — one gesture for "my version
   * stands".
   */
  async keepMine(entity: string, recordId: Uuid, fieldName: string): Promise<void> {
    const key = fieldKey(entity, recordId, fieldName);
    const held = this.heldOff.get(key);
    if (!held) return;
    const outcome = this.locks.has(key)
      ? await this.opts.leaseClient.steal(key)
      : await this.opts.leaseClient.claim(key);
    if (!outcome.ok) { this.notify(); return; }
    this.locks.delete(key);
    this.heldOff.delete(key);
    await this.commitAndRelease(entity, recordId, fieldName, held);
  }

  /** Debounced intermediate value for viewers — never persisted. */
  preview(entity: string, recordId: Uuid, fieldName: string, value: FieldValue): void {
    const key = fieldKey(entity, recordId, fieldName);
    // Remembered while this tab holds the field: if it is taken over, this
    // is the value that must not be lost.
    if (this.isMine(key)) this.drafts.set(key, value);
    this.opts.transport.send({ t: 'preview', key, value });
  }

  async commitAndRelease(entity: string, recordId: Uuid, fieldName: string, value: FieldValue | null): Promise<void> {
    const key = fieldKey(entity, recordId, fieldName);
    const leaseState = this.opts.leaseClient.state;
    const leaseId = (leaseState.phase === 'held' || leaseState.phase === 'disconnected') && leaseState.key === key
      ? leaseState.leaseId
      : undefined;

    /*
     * Whether this is a write at all, asked BEFORE the queued remote change
     * is applied — the question is whether the user changed what the user
     * was looking at.
     *
     * Asking afterwards would compare their untouched value against somebody
     * else's new one, call that a change, and write the old value back over
     * it. A no-op blur would silently undo a remote edit, which is the
     * opposite of what the queue is for.
     */
    const unchanged = value !== null && sameFieldValue(this.opts.replica.getField(key)?.value, value);

    const queued = this.queuedRemote.get(key);
    if (queued) {
      // Hard invariant 5: the queued remote change is applied now, on
      // release, with a notice — our own write below still uses the base we
      // were actually editing against, so a genuine conflict surfaces
      // through the normal stale-base review path rather than being guessed at here.
      await this.opts.replica.applyCommitted(queued);
      this.queuedRemote.delete(key);
      this.notice('This field changed while you were editing it.');
    }

    /*
     * A field that was focused and left alone is not an edit.
     *
     * Every form on these pages claims on focus and commits on blur, and a
     * `<dialog>` focuses its first field the moment it opens. So opening a
     * wallet to look at it, and closing it again, wrote "Renovation" over
     * "Renovation" and left a history row saying so — five of them on the
     * test ledger before anybody noticed what they were.
     *
     * The lease is still released below, because it was still held.
     */
    if (value !== null && !unchanged) {
      const base = this.opts.replica.getField(key)?.version ?? 0;
      const clock = this.hlc.send();
      await this.sendOrQueue(
        { t: 'write', key, value, base, clock, ...(leaseId ? { leaseId } : {}) },
        { key, value, base, clock },
      );
    }

    this.drafts.delete(key);
    this.heldOff.delete(key);
    await this.opts.leaseClient.release(key);
    this.notify();
  }

  /** Compare-and-swap, no lease (ADR-0007) — correct for a toggle with no meaningful "duration". */
  async toggle(entity: string, recordId: Uuid, fieldName: string): Promise<void> {
    const key = fieldKey(entity, recordId, fieldName);
    const snap = this.opts.replica.getField(key);
    const current = snap?.value;
    if (!current || current.t !== 'bool') throw new Error(`${key}: toggle() requires a boolean field`);
    const value: FieldValue = { t: 'bool', v: !current.v };
    const base = snap?.version ?? 0;
    const clock = this.hlc.send();
    await this.sendOrQueue({ t: 'write', key, value, base, clock }, { key, value, base, clock });
    this.notify();
  }

  /**
   * Set a toggle to a known state, rather than flipping whatever is there.
   *
   * `toggle` is right for a switch somebody is pressing: the value on screen
   * is the value in the replica, and flipping it is exactly the gesture. A
   * FORM is a different thing — it says what the answer should be, and it
   * may be saved before the field has ever been written or before its
   * current value has arrived. `toggle` throws in that case, because there
   * is nothing to flip.
   *
   * So this writes the wanted value against whatever base it has, including
   * none: a field never written is at version 0, which is what a first write
   * compares against. Already-equal is a no-op rather than a write, so
   * saving a form without touching the switch costs nothing and writes no
   * history.
   */
  async setToggle(
    entity: string, recordId: Uuid, fieldName: string, wanted: boolean,
  ): Promise<void> {
    const key = fieldKey(entity, recordId, fieldName);
    const snap = this.opts.replica.getField(key);
    if (snap?.value?.t === 'bool' && snap.value.v === wanted) return;
    const value: FieldValue = { t: 'bool', v: wanted };
    const base = snap?.version ?? 0;
    const clock = this.hlc.send();
    await this.sendOrQueue({ t: 'write', key, value, base, clock }, { key, value, base, clock });
    this.notify();
  }

  // --------------------------------------------------------- document-mode

  async claimRecord(entity: string, recordId: Uuid): Promise<LeaseOutcome> {
    const outcome = await this.opts.leaseClient.claim(recordLeaseKey(entity, recordId));
    this.notify();
    return outcome;
  }

  releaseRecord(entity: string, recordId: Uuid): void {
    void this.opts.leaseClient.release(recordLeaseKey(entity, recordId));
    this.notify();
  }

  async commitRecord(entity: string, recordId: Uuid, fields: Record<string, FieldValue>): Promise<void> {
    const key = recordLeaseKey(entity, recordId);
    const leaseState = this.opts.leaseClient.state;
    if ((leaseState.phase !== 'held' && leaseState.phase !== 'disconnected') || leaseState.key !== key) {
      throw new Error(`${entity}:${recordId}: no held lease to commit under`);
    }
    const baseVersion = this.opts.replica.getRecord(entity, recordId)[0]?.version ?? 0;

    if (this.online) {
      const result = await this.opts.commitDocumentRest({
        entity, recordId, fields, baseVersion, leaseId: leaseState.leaseId,
      });
      if (!result.ok) {
        this.notice(`Could not save: ${result.reason ?? 'unknown error'}`);
        return;
      }
    } else {
      await this.opts.outbox.enqueueRecord({ entity, recordId, fields, baseVersion, clock: this.hlc.send() });
    }
    await this.opts.leaseClient.release(key);
    this.notify();
  }

  // ----------------------------------------------------------------- review

  async resolveReview(key: FieldKeyString, keep: 'mine' | 'theirs'): Promise<void> {
    const resolved = this.opts.review.resolveStaleBase(key, keep);
    if (resolved) {
      const clock = this.hlc.send();
      await this.sendOrQueue(
        { t: 'write', key, value: resolved.value, base: resolved.base, clock },
        { key, value: resolved.value, base: resolved.base, clock },
      );
    }
    this.notify();
  }

  async resolvePendingRevision(id: Uuid, resolution: 'applied' | 'discarded'): Promise<void> {
    await this.opts.review.resolvePendingRevision(id, resolution);
    this.notify();
  }

  // ------------------------------------------------------------------- misc

  private requireEntity(name: string): EntityDef {
    const e = this.opts.manifest.entities[name];
    if (!e) throw new Error(`unknown entity '${name}'`);
    return e;
  }

  private requireField(entity: EntityDef, name: string): FieldDef {
    const f = entity.fields[name];
    if (!f) throw new Error(`unknown field '${name}'`);
    return f;
  }

  private isMine(key: FieldKeyString): boolean {
    const s = this.opts.leaseClient.state;
    return (s.phase === 'claiming' || s.phase === 'held' || s.phase === 'disconnected') && s.key === key;
  }

  private notify(): void { for (const fn of this.listeners) fn(); }
  private notice(message: string): void { for (const fn of this.noticeListeners) fn(message); }

  private async sendOrQueue(
    msg: Extract<ClientMessage, { t: 'write' }>,
    queueEntry: { key: FieldKeyString; value: FieldValue; base: number; clock: Hlc },
  ): Promise<void> {
    const sent = this.online && this.opts.transport.send(msg);
    if (!sent) await this.opts.outbox.enqueue(queueEntry);
  }

  private onTransportStateChange(s: TransportState): void {
    this.online = s === 'open';
    if (this.online) void this.replayOutbox();
    this.notify();
  }

  /** Replays queued writes in HLC order — the outbox's own ordering guarantee. */
  private async replayOutbox(): Promise<void> {
    for (const w of await this.opts.outbox.all()) {
      if (!this.online) return; // dropped again mid-replay; the rest stays queued, safe to retry from scratch
      const sent = this.opts.transport.send({ t: 'write', key: w.key, value: w.value, base: w.base, clock: w.clock });
      if (sent) await this.opts.outbox.remove(w.id);
      else return;
    }
  }

  private async onMessage(msg: ServerMessage): Promise<void> {
    switch (msg.t) {
      case 'presence':
        this.presenceByRecord.set(`${msg.entity}:${msg.recordId}`, msg.users);
        this.notify();
        return;
      case 'leaseTaken': {
        // Taken off this tab: keep what was typed, before anything repaints
        // the field with theirs (SPEC §6.1, ADR-0003 2026-09-12).
        if (msg.stolenFrom === this.opts.me.id && this.heldKey === msg.key) {
          const draft = this.drafts.get(msg.key);
          if (draft && !sameFieldValue(draft, this.opts.replica.getField(msg.key)?.value)) {
            this.heldOff.set(msg.key, draft);
          }
          this.drafts.delete(msg.key);
          this.heldKey = undefined;
        }
        this.locks.set(msg.key, { holder: msg.holder, online: msg.online, since: msg.since });
        this.notify();
        return;
      }
      case 'leaseFreed':
        this.locks.delete(msg.key);
        this.notify();
        return;
      case 'committed':
        await this.applyCommitted(msg.version);
        return;
      case 'rejected':
        this.handleRejected(msg.key, msg.reason, msg.current);
        return;
      case 'welcome':
      case 'preview':
      case 'recordEvent':
      case 'schemaStale':
      case 'error':
      case 'granted':
      case 'denied':
        // 'granted'/'denied' are consumed directly by LeaseClient; the rest
        // need no session-level reaction beyond the generic re-render.
        this.notify();
        return;
    }
  }

  private async applyCommitted(version: FieldVersion): Promise<void> {
    if (this.isMine(version.key)) {
      this.queuedRemote.set(version.key, version);
      await this.opts.replica.recordHistoryOnly(version);
      this.notify();
      return;
    }
    await this.opts.replica.applyCommitted(version);
    this.markRecentlyChanged(version.key);
    this.notify();
  }

  private markRecentlyChanged(key: FieldKeyString): void {
    const existing = this.recentlyChanged.get(key);
    if (existing !== undefined) clearTimeout(existing);
    this.recentlyChanged.set(key, setTimeout(() => {
      this.recentlyChanged.delete(key);
      this.notify();
    }, RECENTLY_CHANGED_MS));
  }

  private handleRejected(key: FieldKeyString, reason: RejectReason, current?: FieldVersion): void {
    if (reason === 'stale-base' && current) {
      const snap = this.opts.replica.getField(key);
      this.opts.review.addStaleBase({
        key,
        mine: snap?.value ?? current.value,
        mineClock: snap?.clock ?? current.clock,
        theirs: current,
        origin: 'online-race',
      });
    } else {
      this.notice(`Could not save: ${reason}`);
    }
    this.notify();
  }
}
